import { isPipelineType, PipelineSeriesByRegionColour, PipelineSeriesOnBuild, PipelineType } from '@nova/types';

import { TexturePipelineBase } from './glPipelines/pipeline-texBase';
import { SummationTexturePipeline } from './glPipelines/pipeline-texture-summation';
import { DeleteTexture } from './glPipelines/utils/pipeline-utils-gl';
import {
  PipelineSeriesEntry_UniformParams,
  pipelineSeriesParamConversion,
  PipelineSeriesToRender,
  TexturePipelineSet
} from './glPipelines/utils/pipeline-utils-types';
import { IScaleParams } from './models/scale-params.interface';
import { IScalePipelineResult } from './models/scale-pipeline-result.interface';
import { Canvas2DRenderPipeline } from './render-pipelines/canvas2d-render-pipeline';
import { createRenderPipeline } from './render-pipelines/create-render-pipeline';
import { IRenderPipeline } from './render-pipelines/render-pipeline.interface';
import { canUseRepeatWrapForTexture, getWebGLCapabilities, isWebGLPipelineError, logWebGLFallback } from './render-pipelines/webgl-support';
import { PipelineSetByColourRegion } from './texture-manip-drawSeriesByRegion';

type TextureSeriesOutput = { target: 'canvas' } | { target: 'texture'; scaleDownFinalRegionMaskResult: boolean };

interface PipelineOptions {
  operationName?: string;
  preferCanvas2D?: () => boolean;
  fallbackReason?: string;
}

/**
 * Static image-processing operations. Most callers use the series-based entry points
 * rather than invoking individual transforms directly.
 */
export class StaticPipelineOperations {
  private static _sharedPipeline: IRenderPipeline | undefined;

  public static setSharedPipeline(pipeline: IRenderPipeline | undefined): void {
    StaticPipelineOperations._sharedPipeline = pipeline;
  }

  private static resolvePipeline(width: number, height: number, gl?: WebGLRenderingContext): {
    pipeline: IRenderPipeline;
    shouldDestroy: boolean;
  } {
    if (StaticPipelineOperations._sharedPipeline) {
      StaticPipelineOperations._sharedPipeline.init(width, height);
      return { pipeline: StaticPipelineOperations._sharedPipeline, shouldDestroy: false };
    }

    return {
      pipeline: createRenderPipeline(width, height, gl),
      shouldDestroy: !gl
    };
  }

  private static withPipeline<T>(
    width: number,
    height: number,
    gl: WebGLRenderingContext | undefined,
    operation: (pipeline: IRenderPipeline) => T,
    options?: PipelineOptions
  ): T {
    const operationName = options?.operationName ?? 'WebGL operation';

    if (options?.preferCanvas2D?.()) {
      return StaticPipelineOperations.fallbackToCanvas2D(
        width,
        height,
        gl,
        operation,
        operationName,
        options.fallbackReason ?? 'capability check failed'
      );
    }

    const { pipeline, shouldDestroy } = StaticPipelineOperations.resolvePipeline(width, height, gl);

    try {
      return operation(pipeline);
    }
    catch (error) {
      if (pipeline.type === 'webgl' && isWebGLPipelineError(error)) {
        return StaticPipelineOperations.fallbackToCanvas2D(
          width,
          height,
          gl,
          operation,
          operationName,
          error instanceof Error ? error.message : 'WebGL error'
        );
      }

      throw error;
    }
    finally {
      if (shouldDestroy) {
        pipeline.destroy();
      }
    }
  }

  private static withSourceImagePipeline<T>(
    image: ImageData,
    gl: WebGLRenderingContext | undefined,
    operation: (pipeline: IRenderPipeline) => T,
    options?: PipelineOptions
  ): T {
    return StaticPipelineOperations.withPipeline(image.width, image.height, gl, operation, options);
  }

  private static fallbackToCanvas2D<T>(
    width: number,
    height: number,
    gl: WebGLRenderingContext | undefined,
    operation: (pipeline: IRenderPipeline) => T,
    operationName: string,
    reason: string
  ): T {
    logWebGLFallback({ operation: operationName, reason, width, height, gl });
    const pipeline = new Canvas2DRenderPipeline(width, height);
    try {
      return operation(pipeline);
    }
    finally {
      pipeline.destroy();
    }
  }

  private static bindPipelineForSeriesDraw(
    pipeline: TexturePipelineBase,
    sourceTexture: WebGLTexture,
    regionMaskBaseTexture?: WebGLTexture
  ): void {
    if (regionMaskBaseTexture && pipeline.regionMask) {
      pipeline.regionMask.UpdateBaseTexture(regionMaskBaseTexture);
    }

    pipeline.SetGlTextures([sourceTexture]);
    pipeline.UseProgram();
  }

  public static GetTiled(imageData: ImageData, finalWidth: number, finalHeight: number, gl?: WebGLRenderingContext): Uint8Array {
    return StaticPipelineOperations.withPipeline(
      finalWidth,
      finalHeight,
      gl,
      (pipeline) => pipeline.tile(imageData, finalWidth, finalHeight),
      {
        operationName: 'tile',
        preferCanvas2D: () => !!gl && !canUseRepeatWrapForTexture(gl, imageData.width, imageData.height),
        fallbackReason: `NPOT source texture <${imageData.width}x${imageData.height}> cannot use REPEAT wrap on WebGL 1`
      }
    );
  }

  public static GetMasked(imageToMaskData: ImageData, imageAsMaskData: ImageData, invertMaskEffect: boolean, gl?: WebGLRenderingContext): Uint8Array {
    return StaticPipelineOperations.withSourceImagePipeline(imageToMaskData, gl, (pipeline) =>
      pipeline.mask(imageToMaskData, imageAsMaskData, invertMaskEffect));
  }

  public static GetOverlayed(imageBottomData: ImageData, imageTopData: ImageData, gl?: WebGLRenderingContext): Uint8Array {
    return StaticPipelineOperations.withSourceImagePipeline(imageBottomData, gl, (pipeline) =>
      pipeline.overlay(imageBottomData, imageTopData));
  }

  public static GetColourTinted(imageData: ImageData, colour: number[], gl?: WebGLRenderingContext): Uint8Array {
    return StaticPipelineOperations.withSourceImagePipeline(imageData, gl, (pipeline) =>
      pipeline.tint(imageData, colour));
  }

  public static GetScaled(imageData: ImageData, scale: IScaleParams, gl?: WebGLRenderingContext): IScalePipelineResult {
    return StaticPipelineOperations.withSourceImagePipeline(imageData, gl, (pipeline) =>
      pipeline.scale(imageData, scale));
  }

  public static RunPipelineSeriesAsPixels(base: ImageData, seriesBuilt: PipelineSeriesOnBuild, gl?: WebGLRenderingContext): Uint8Array {
    return StaticPipelineOperations.withSourceImagePipeline(base, gl, (pipeline) =>
      pipeline.runPipelineSeries(base, seriesBuilt));
  }

  public static RunPipelineSeriesByRegionColourAsPixels(
    base: ImageData,
    mask: ImageData,
    seriesByRegionColour: PipelineSeriesByRegionColour,
    gl?: WebGLRenderingContext
  ): Uint8Array {
    return StaticPipelineOperations.withSourceImagePipeline(
      base,
      gl,
      (pipeline) => pipeline.runPipelineSeriesByRegionColour(base, mask, seriesByRegionColour),
      {
        operationName: 'runPipelineSeriesByRegionColour',
        preferCanvas2D: () => !!gl && getWebGLCapabilities(gl).maxTextureImageUnits < 3,
        fallbackReason: 'insufficient texture image units for region-mask pipeline (requires 3)'
      }
    );
  }

  /**
   * Converts series entries to render-ready params and splits the final step.
   * The last entry is finalized separately so callers can draw to canvas or texture.
   */
  private static preparePipelineSeriesEntries(seriesBuilt: PipelineSeriesOnBuild): {
    intermediateEntries: PipelineSeriesToRender;
    lastEntry: PipelineSeriesEntry_UniformParams;
  } {
    const pipelineSeriesToRender: PipelineSeriesToRender = [];

    for (const entry of seriesBuilt) {
      if (!isPipelineType(entry.type)) {
        console.error(`StaticPipelineOperations::preparePipelineSeriesEntries: Invalid pipeline type <${entry.type}> in seriesBuilt. Cannot continue.`);
        continue;
      }

      const conversionFunc = pipelineSeriesParamConversion[entry.type];
      const params = conversionFunc ? conversionFunc(entry.params) : entry.params;

      pipelineSeriesToRender.push({
        type: entry.type,
        params
      });
    }

    const lastEntry = pipelineSeriesToRender.pop();
    if (!lastEntry) {
      throw new Error('StaticPipelineOperations:preparePipelineSeriesEntries lastEntry is undefined, should be impossible at this point.');
    }

    return {
      intermediateEntries: pipelineSeriesToRender,
      lastEntry
    };
  }

  /**
   * Runs intermediate series steps into offscreen textures, then finalizes to canvas or texture.
   */
  private static runTexturePipelineSeries(
    texturePipelines: TexturePipelineSet,
    seriesBuilt: PipelineSeriesOnBuild,
    output: TextureSeriesOutput
  ): PipelineType | WebGLTexture {
    const defaultPipeline = texturePipelines[PipelineType.textureDefault];

    if (seriesBuilt.length === 0) {
      defaultPipeline.UseProgram();
      if (output.target === 'canvas') {
        defaultPipeline.Draw({});
        return PipelineType.textureDefault;
      }

      return defaultPipeline.DrawToTexture({});
    }

    const { intermediateEntries, lastEntry } = StaticPipelineOperations.preparePipelineSeriesEntries(seriesBuilt);
    const gl = defaultPipeline.Gl;
    defaultPipeline.UseProgram();
    let nextTexture: WebGLTexture = defaultPipeline.DrawToTexture({});
    let regionMaskNextTex: WebGLTexture | undefined;

    for (const entry of intermediateEntries) {
      const pipeline = texturePipelines[entry.type];
      StaticPipelineOperations.bindPipelineForSeriesDraw(pipeline, nextTexture, regionMaskNextTex);

      const previousTexture = nextTexture;
      nextTexture = pipeline.DrawToTexture(entry.params);
      if (previousTexture !== defaultPipeline.glTextures[0]) {
        DeleteTexture(gl, previousTexture);
      }
      regionMaskNextTex = nextTexture;
    }

    const lastPipeline = texturePipelines[lastEntry.type];
    StaticPipelineOperations.bindPipelineForSeriesDraw(lastPipeline, nextTexture, regionMaskNextTex);

    let result: PipelineType | WebGLTexture;
    if (output.target === 'canvas') {
      lastPipeline.Draw(lastEntry.params);
      result = lastEntry.type;
    }
    else {
      if (lastPipeline.regionMask) {
        lastPipeline.regionMask.scaleDownResult = output.scaleDownFinalRegionMaskResult;
      }
      result = lastPipeline.DrawToTexture(lastEntry.params);
    }

    StaticPipelineOperations.deleteSeriesIntermediateTexture(defaultPipeline.Gl, nextTexture, defaultPipeline);
    return result;
  }

  private static sumTexturesAdditive(
    sumPipeline: SummationTexturePipeline,
    previousTexture: WebGLTexture,
    nextTexture: WebGLTexture
  ): WebGLTexture {
    sumPipeline.SetGlTextures([previousTexture, nextTexture]);
    sumPipeline.UseProgram();
    const summedTexture = sumPipeline.DrawToTexture({});
    DeleteTexture(sumPipeline.Gl, previousTexture);
    if (nextTexture !== summedTexture) {
      DeleteTexture(sumPipeline.Gl, nextTexture);
    }
    return summedTexture;
  }

  private static deleteSeriesIntermediateTexture(
    gl: WebGLRenderingContext,
    texture: WebGLTexture,
    defaultPipeline: TexturePipelineBase
  ): void {
    if (texture !== defaultPipeline.glTextures[0]) {
      DeleteTexture(gl, texture);
    }
  }

  public static DrawPipelineSeriesToCanvas(texturePipelines: TexturePipelineSet, seriesBuilt: PipelineSeriesOnBuild): PipelineType {
    return StaticPipelineOperations.runTexturePipelineSeries(texturePipelines, seriesBuilt, { target: 'canvas' }) as PipelineType;
  }

  private static drawPipelineSeriesToTexture(
    texturePipelines: TexturePipelineSet,
    seriesBuilt: PipelineSeriesOnBuild,
    scaleDownFinalRegionMaskResult: boolean
  ): WebGLTexture {
    return StaticPipelineOperations.runTexturePipelineSeries(
      texturePipelines,
      seriesBuilt,
      { target: 'texture', scaleDownFinalRegionMaskResult }
    );
  }

  private static resetRegionMaskPipelines(pipelineSet: TexturePipelineSet, baseTexture: WebGLTexture): void {
    for (const type in pipelineSet) {
      if (!Object.prototype.hasOwnProperty.call(pipelineSet, type)) { continue; }
      const regionMaskComp = pipelineSet[type].regionMask;
      if (!regionMaskComp) { continue; }

      regionMaskComp.UpdateBaseTexture(baseTexture);
      regionMaskComp.scaleDownResult = false;
    }
  }

  public static RunPipelineSeriesByRegionColour(
    pipelinesByColourRegion: PipelineSetByColourRegion,
    sumPipeline: SummationTexturePipeline,
    seriesByRegionColour: PipelineSeriesByRegionColour,
    baseTexture: WebGLTexture): WebGLTexture | undefined {
    let prevTextureOutput: WebGLTexture | undefined;

    for (const colourName in seriesByRegionColour) {
      if (!Object.prototype.hasOwnProperty.call(seriesByRegionColour, colourName)) { continue; }

      const pipelineSet = pipelinesByColourRegion[colourName];
      if (!pipelineSet) {
        console.error(`A set of pipelines was not established for the colour <${colourName}>`);
        return undefined;
      }

      StaticPipelineOperations.resetRegionMaskPipelines(pipelineSet, baseTexture);

      const seriesData: PipelineSeriesOnBuild = seriesByRegionColour[colourName];
      let textureResult = StaticPipelineOperations.drawPipelineSeriesToTexture(pipelineSet, seriesData, true);

      if (prevTextureOutput) {
        textureResult = StaticPipelineOperations.sumTexturesAdditive(sumPipeline, prevTextureOutput, textureResult);
      }

      prevTextureOutput = textureResult;
    }

    return prevTextureOutput;
  }
}
